import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useQueueStore } from "@/lib/store";
import { usePatientsStore } from "@/lib/stores/patients";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Patient } from "@/types";
import { useAuthStore } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, User, Phone, Calendar, MapPin, History } from "lucide-react";
import { formatPatientId } from '@/lib/utils/patientId';
import { safeFormatDateOnly } from '@/lib/utils/dateUtils';
import { PatientSearchDialog } from './PatientSearchDialog';
import { validateAge, normalizeAgeString } from '@/lib/utils/ageUtils';


export function PatientRegistrationForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isStaff = user?.role === "staff";
  const { 
    addPatient, 
    patients, 
    fetchPatients,
    fetchMasterPatients,
    findOrCreateMasterPatient,
    createConsultation,
    updatePatient
  } = useQueueStore();
  
  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  
  // History dialog state
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Age validation state
  const [ageError, setAgeError] = useState<string>("");
  const [ageUnit, setAgeUnit] = useState<string>("years");
  
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "male",
    contactNumber: "",
    priority: "normal",
    reason: "",
    referralDoctor: "",
    location: "",
  });

  useEffect(() => {
    fetchPatients();
    fetchMasterPatients();
  }, [fetchPatients, fetchMasterPatients]);

  // Auto-search as user types (with debounce)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const searchTimer = setTimeout(() => {
      const query = searchQuery.toLowerCase().trim();
      const results = patients.filter(patient =>
        patient.name.toLowerCase().includes(query) ||
        patient.contactNumber.includes(query)
      );

      const sortedResults = results.sort((a, b) =>
        new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime()
      );

      setSearchResults(sortedResults.slice(0, 5));
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [searchQuery, patients]);

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    const normalizedAge = normalizeAgeString(patient.age);
    let ageNum = normalizedAge;
    let unit = "years";

    if (normalizedAge.includes("months")) {
      ageNum = normalizedAge.replace(/\s*months?/i, "").trim();
      unit = "months";
    } else if (normalizedAge.includes("weeks")) {
      ageNum = normalizedAge.replace(/\s*weeks?/i, "").trim();
      unit = "weeks";
    } else {
      ageNum = normalizedAge.replace(/\s*years?/i, "").trim();
    }

    setFormData({
      name: patient.name,
      age: ageNum,
      gender: patient.gender,
      contactNumber: patient.contactNumber,
      priority: "normal", // Reset for new visit
      reason: "", // Reset for new visit
      referralDoctor: patient.referralDoctor || "",
      location: patient.location || "",
    });
    setAgeUnit(unit);
    setSearchQuery("");
    
    // Clear any existing age error when selecting a patient
    setAgeError("");
    
    toast({
      title: "Patient Selected",
      description: `Using data for ${patient.name}. Please update reason for this visit.`,
    });
  };

  const handleClearSelection = () => {
    setSelectedPatient(null);
    setFormData({
      name: "",
      age: "",
      gender: "male",
      contactNumber: "",
      priority: "normal",
      reason: "",
      referralDoctor: "",
      location: "",
    });
    setAgeUnit("years");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "name") {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else if (name === "age") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear age error when user starts typing
      if (ageError) {
        setAgeError("");
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAgeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    // Combine number with selected unit and validate.
    if (value && /^\d+$/.test(value)) {
      const formattedAge = `${value} ${ageUnit}`;
      setFormData((prev) => ({ ...prev, age: formattedAge }));
      const validation = validateAge(formattedAge);
      if (!validation.isValid) {
        setAgeError(validation.message);
      } else {
        setAgeError("");
      }
    } else if (value) {
      const validation = validateAge(value);
      if (!validation.isValid) {
        setAgeError(validation.message);
      } else {
        setAgeError("");
      }
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate age format
    const ageValidation = validateAge(formData.age);
    if (!ageValidation.isValid) {
      setAgeError(ageValidation.message);
      toast({
        title: "Error",
        description: ageValidation.message,
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.name || !formData.age || !formData.contactNumber || !formData.reason) {
      toast({
        title: "Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine age number with selected unit when needed.
      let finalAge = formData.age;
      if (formData.age && /^\d+$/.test(formData.age.trim())) {
        finalAge = `${formData.age.trim()} ${ageUnit}`;
      }

      // Step 1: Create or find master patient
      const masterPatientId = await findOrCreateMasterPatient({
        name: formData.name,
        age: finalAge,
        gender: formData.gender as any,
        contactNumber: formData.contactNumber,
        location: formData.location,
        referralDoctor: formData.referralDoctor,
      });

      let patientId: string | undefined = undefined;
      let registeredAt: string | undefined = undefined;

      if (selectedPatient) {
        // Existing patient: use their ID and set registeredAt to now for new visit
        patientId = selectedPatient.id;
        registeredAt = new Date().toISOString(); // New visit time

        // Update patient status and visit info so they appear in the queue
        await updatePatient(selectedPatient.id, {
          status: 'waiting',
          reason: formData.reason,
          priority: formData.priority as any,
          registeredAt,
        });
        // Refetch patients to ensure local state is up to date
        await fetchPatients();
      } else {
        // New patient: add to queue with registeredAt set to now
        registeredAt = new Date().toISOString();
        const ageNumber = parseInt(formData.age.trim()) || 0;
        await addPatient({
          name: formData.name,
          age: String(ageNumber),
          gender: formData.gender as any,
          contactNumber: formData.contactNumber,
          priority: formData.priority as any,
          reason: formData.reason,
          referralDoctor: formData.referralDoctor,
          location: formData.location,
          registeredAt,
        });
        const newPatient = usePatientsStore.getState().patients.find(
          (p) =>
            p.name === formData.name &&
            p.contactNumber === formData.contactNumber &&
            p.reason === formData.reason
        );
        if (newPatient) {
          patientId = newPatient.id;
          registeredAt = newPatient.registeredAt;
        }
        void usePatientsStore.getState().fetchPatients();
      }

      if (patientId && registeredAt) {
        // Create consultation record linking patient to master
        await createConsultation({
          masterPatientId,
          patientId,
          reason: formData.reason,
          priority: formData.priority as any,
          registeredAt,
          notes: formData.reason,
        });
      }

      toast({
        title: "Success",
        description: `Patient ${formData.name} has been registered${selectedPatient ? ' for new visit' : ''}`,
      });

      // Staff lands directly on patient details after registration.
      if (isStaff && patientId) {
        setTimeout(() => {
          navigate(`/patient/${patientId}`);
        }, 500);
      } else {
        handleClearSelection();
      }
    } catch (error) {
      console.error('Error registering patient:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to register patient. Please try again.';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header with History Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Patient Registration</h2>
          <p className="text-gray-600">Register new patients or search for existing ones</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistoryDialog(true)}
          className="flex items-center gap-2"
        >
          <History className="h-4 w-4" />
          View Patient History
        </Button>
      </div>

      {/* Patient Search Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <Search className="h-6 w-6 text-gray-500" />
              <span className="font-semibold text-base">Search Existing Patients:</span>
            </div>
            <Input
              placeholder="Search by patient name or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 max-w-lg text-base h-11"
            />
            {isSearching && (
              <div className="text-base text-gray-500 flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                Searching...
              </div>
            )}
          </div>
          
          {/* Search Results */}
          {searchQuery.trim() && (
            <div className="mt-4 space-y-2">
              {isSearching ? (
                <div className="text-center py-4 text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mx-auto mb-2"></div>
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((patient) => (
                  <div
                    key={patient.id}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleSelectPatient(patient)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-gray-500" />
                        <div>
                          <div className="font-medium">{patient.name}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {patient.contactNumber}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {safeFormatDateOnly(patient.registeredAt)}
                            </span>
                            {patient.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {patient.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs">
                          {formatPatientId(patient.id)}
                        </Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          {patient.age}, {patient.gender}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No patients found. Try a different search term.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Patient Info */}
      {selectedPatient && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800">
                  Using existing patient: {selectedPatient.name}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSelection}
                className="text-red-600 hover:text-red-700"
              >
                Clear Selection
              </Button>
            </div>
            <div className="text-sm text-green-700 mt-2">
              Previous visit: {safeFormatDateOnly(selectedPatient.registeredAt)} - {selectedPatient.reason}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Form */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedPatient ? "New Visit Registration" : "Patient Registration"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Patient Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter patient name"
                  className="uppercase"
                  disabled={!!selectedPatient}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="age">Age <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    id="age"
                    name="age"
                    type="text"
                    value={formData.age}
                    onChange={handleChange}
                    onBlur={handleAgeBlur}
                    placeholder="e.g., 21"
                    className={`flex-1 ${ageError ? "border-red-500" : ""}`}
                  />
                  <Select value={ageUnit} onValueChange={setAgeUnit}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="years">Years</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {ageError && (
                  <p className="text-sm text-red-500">{ageError}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Gender <span className="text-red-500">*</span></Label>
                <RadioGroup
                  defaultValue="male"
                  value={formData.gender}
                  onValueChange={(value) => handleSelectChange("gender", value)}
                  className="flex gap-6"
                  disabled={!!selectedPatient}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female">Female</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="other" id="other" />
                    <Label htmlFor="other">Other</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contactNumber">Contact Number <span className="text-red-500">*</span></Label>
                <Input
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="Enter 10-digit contact number"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  type="tel"
                  disabled={!!selectedPatient}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => handleSelectChange("priority", value)}
                >
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralDoctor">Referral Doctor</Label>
                <Input
                  id="referralDoctor"
                  name="referralDoctor"
                  value={formData.referralDoctor}
                  onChange={handleChange}
                  placeholder="Enter referral doctor name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter patient's location"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Visit <span className="text-red-500">*</span></Label>
                <Textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Briefly describe the reason for the visit"
                  rows={3}
                />
              </div>
            </div>

            
            <Button 
              type="submit" 
              className="w-full bg-hospital-primary hover:bg-hospital-primary/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : (selectedPatient ? "Register New Visit" : "Register Patient")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Patient History Dialog */}
      <PatientSearchDialog
        isOpen={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        showHistoryButton={true}
      />
    </div>
  );
}
